import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import Chip from '@src/app/chip'
import { useAppStore } from '@src/app/context'
import { useFlag } from '@src/app/feature-flags'
import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import SelectionHeader from '@src/app/voice/ongoing-call/SelectionHeader'
import { useCallContext } from '@src/app/voice/ongoing-call/context/useCallContext'
import { ExternalParticipantBanner } from '@src/app/voice/ongoing-call/external-participant-banner'
import { MessageTextInput } from '@src/app/voice/ongoing-call/message-text-input'
import Button from '@src/component/ButtonV2'
import type { ChipInputProps } from '@src/component/chip-input'
import PhoneNumberSelector from '@src/component/phone-number-selector/PhoneNumberSelector'
import { MemberRow } from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import type { IdentityRowInstance } from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import useKeyboardShortcuts from '@src/lib/hooks/useKeyboardShortcuts'
import type { Identity } from '@src/service/model'

import * as styles from './AddParticipant.css'

interface AddParticipantProps {
  onClose: () => void
}

const AddParticipant = function ({ onClose }: AddParticipantProps) {
  const {
    addParticipantController: controller,
    openConfirmationModal,
    call,
  } = useCallContext()
  const { inboxes, toast } = useAppStore()
  const { blurred } = useCallsDialogContext()
  const groupCallingAddMultipleParticipantsFlagEnabled = useFlag(
    'groupCallingAddMultipleParticipants',
  )
  const phoneNumbers = inboxes.phoneNumbers

  const [phoneNumber, setPhoneNumber] = useState(call.phoneNumber || phoneNumbers[0])

  const handleBack = () => {
    controller.handleBack()
    onClose()
  }

  const handleAdd = () => {
    controller
      .handleAdd(controller.hasExternalSelections ? phoneNumber?.id : undefined)
      ?.catch(toast.showError)
    handleBack()
  }

  const handlePhoneNumberSelection = (phoneNumberIdForExternalParticipant: string) => {
    const number = phoneNumbers.find((p) => p.id === phoneNumberIdForExternalParticipant)
    if (number) {
      setPhoneNumber(number)
    }
  }

  const renderChip: ChipInputProps<IdentityRowInstance>['renderChip'] = (row, states) => {
    const handleRemove = () => {
      if (row.selection) {
        controller.clearSelection(row.selection)
      }
    }

    return (
      <Chip
        size={24}
        identity={row.identity as Identity}
        {...states}
        isListener={row instanceof MemberRow ? controller.listeners.includes(row) : false}
        onRemove={handleRemove}
      />
    )
  }

  useKeyboardShortcuts({
    name: 'AddParticipant',
    node:
      controller.messageFocused && controller.messageRef.current
        ? controller.messageRef.current
        : controller.focusedNode,
    filter: (_, e) => !e.defaultPrevented,
    handler: (shortcut, event) => {
      if (shortcut === 'Escape' && !openConfirmationModal) {
        if (controller.messageFocused) {
          controller.messageRef.current?.blur()
        } else {
          handleBack()
          event.preventDefault()
        }
      } else if (shortcut === 'Enter') {
        if (controller.messageFocused && controller.message) {
          handleAdd()
          event.preventDefault()
        }
      }
    },
    dep: [controller],
  })

  useEffect(() => {
    return () => {
      controller.handleBack()
    }
  }, [controller])

  return (
    <>
      <SelectionHeader
        title="Add people to this call"
        description="Invite other teammates, contacts, or even phone numbers to join this call."
        handleBack={handleBack}
      />
      <div className={styles.root}>
        <div className={cx(styles.selector, { [styles.selectorBlurred]: blurred })}>
          <PhoneNumberSelector
            controller={controller}
            overlayThemeKey="dark"
            renderChip={renderChip}
          />
        </div>
        <div className={styles.controls}>
          {controller.hasInternalSelections ? (
            <MessageTextInput controller={controller} />
          ) : (
            <div style={{ flexGrow: 1 }}></div>
          )}
          {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
          <Button
            disabled={
              controller.selections.length === 0 || controller.maxMessageLengthReached
            }
            height={35}
            onClick={handleAdd}
            className={blurred ? styles.buttonBlurred : undefined}
          >
            Add
            {groupCallingAddMultipleParticipantsFlagEnabled &&
            controller.selections.length > 0 ? (
              <span className={styles.buttonCount}>{controller.selections.length}</span>
            ) : null}
          </Button>
        </div>
        <ExternalParticipantBanner
          controller={controller}
          // @ts-expect-error unchecked index access
          phoneNumber={phoneNumber}
          showChange={phoneNumbers.length > 1}
          onChange={handlePhoneNumberSelection}
        />
      </div>
    </>
  )
}

export default observer(AddParticipant)
